import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaLinkedin } from "react-icons/fa";
import { FaHeart } from "react-icons/fa";
import { MdOutlineEmail, MdNewReleases } from "react-icons/md";
import { StarryBackground } from "./StarryBackground";
import { isFeatureEnabled } from "../config/featureFlags";
import { ChangelogModal } from "./ChangelogModal";
import packageJson from "../../package.json";

export function About() {
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [logoVisible, setLogoVisible] = useState(false);
  const [textVisible, setTextVisible] = useState(false);
  const [backVisible, setBackVisible] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);
  const [ripples, setRipples] = useState<{
    [key: string]: { x: number; y: number; timestamp: number } | null;
  }>({
    back: null,
    kofi: null,
    whatsNew: null,
  });

  // Check theme from document attribute
  useEffect(() => {
    const checkTheme = () => {
      const theme = document.documentElement.getAttribute("data-theme");
      setIsDarkMode(theme !== "light");
    };

    checkTheme();

    // Watch for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  // Staggered reveal sequence for logo -> text -> back button -> footer
  useEffect(() => {
    const t1 = setTimeout(() => setLogoVisible(true), 200);
    const t2 = setTimeout(() => setTextVisible(true), 600);
    const t3 = setTimeout(() => setBackVisible(true), 1000);
    const t4 = setTimeout(() => setFooterVisible(true), 1400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);

  const handleButtonClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    buttonKey: string
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setRipples((prev) => ({
      ...prev,
      [buttonKey]: { x, y, timestamp: Date.now() },
    }));

    // Clear ripple after animation
    setTimeout(() => {
      setRipples((prev) => ({
        ...prev,
        [buttonKey]: null,
      }));
    }, 600);
  };

  return (
    <div className="about-page">
      <StarryBackground isDarkMode={isDarkMode} />

      <div className="about-container">
        <div className="about-content">
          <img
            src={
              isDarkMode ? "/polytrome_logo.svg" : "/polytrome_logo_black.svg"
            }
            alt="Polytrome"
            className={`about-logo fade-in-up ${logoVisible ? "visible" : ""}`}
          />

          <div className="back-button-container">
            <Link
              to="/"
              className={`back-button fade-in-up ${
                backVisible ? "visible" : ""
              }`}
              onClick={(e) => handleButtonClick(e as any, "back")}
            >
              {ripples.back && (
                <span
                  className="button-ripple"
                  style={{
                    left: ripples.back.x,
                    top: ripples.back.y,
                  }}
                />
              )}
              {FaArrowLeft({})}
              Back to Polytrome
            </Link>
            <button
              className={`back-button whats-new-button fade-in-up ${
                backVisible ? "visible" : ""
              }`}
              onClick={(e) => {
                handleButtonClick(e as any, "whatsNew");
                setIsChangelogOpen(true);
              }}
            >
              {ripples.whatsNew && (
                <span
                  className="button-ripple"
                  style={{
                    left: ripples.whatsNew.x,
                    top: ripples.whatsNew.y,
                  }}
                />
              )}
              {MdNewReleases({})}
              What's New
            </button>
            <a
              href="https://ko-fi.com/polytrome"
              target="_blank"
              rel="noopener noreferrer"
              className={`back-button kofi-button fade-in-up ${
                backVisible ? "visible" : ""
              }`}
              onClick={(e) => handleButtonClick(e, "kofi")}
            >
              {ripples.kofi && (
                <span
                  className="button-ripple"
                  style={{
                    left: ripples.kofi.x,
                    top: ripples.kofi.y,
                  }}
                />
              )}
              <img src="/kofiLogo.webp" alt="Ko-fi" className="kofi-logo" />
              Support on Ko-fi
            </a>
          </div>

          <div
            className={`about-section fade-in-up ${
              textVisible ? "visible" : ""
            }`}
          >
            <h2>About</h2>
            <p>
              Polytrome is a free and versatile metronome web app. It supports a
              plethora of useful features, including custom time signatures,
              subdivisions, progressive tempo changes, custom song structures, a
              timer, changeable click sounds, and much more. Polytrome's goal is
              to provide a comprehensive musical practice experience for
              musicians of all levels.
            </p>
          </div>

          <div
            className={`about-section fade-in-up ${
              textVisible ? "visible" : ""
            }`}
          >
            <h2>Features</h2>
            <ul>
              <li>
                <b>Tempo control</b> - Change the BPM from 30 to 300.{" "}
                <span className="keyboard-shortcut-text">
                  Use the keyboard shortcuts <kbd>↑</kbd> and <kbd>↓</kbd> to
                  adjust the tempo by 1 BPM, or <kbd>Shift + ↑</kbd> and{" "}
                  <kbd>Shift + ↓</kbd> to adjust the tempo by 10 BPM.
                </span>{" "}
                You can use the BPM slider, + and - buttons, or just click on
                the BPM and type it in manually.
              </li>
              <li>
                <b>Custom time signatures</b> - Set custom numerators and
                denominators for your time signature.{" "}
                <span className="keyboard-shortcut-text">
                  Use the keyboard shortcuts <kbd>Ctrl + ↑</kbd> and{" "}
                  <kbd>Ctrl + ↓</kbd> to adjust the beats per bar (numerator),
                  or <kbd>Ctrl + Shift + ↑</kbd> and <kbd>Ctrl + Shift + ↓</kbd>{" "}
                  to adjust the note value (denominator).
                </span>
              </li>
              {isFeatureEnabled("subdivisions") && (
                <li>
                  <b>Subdivisions</b> - Sub-divide each beat of the bar into
                  eighth notes, triplets, quintuplets, sextuplets, or
                  septuplets. Note: while this feature is enabled, changing the
                  BPM, Time Signature, or using Tap Tempo will be disabled.{" "}
                  <span className="keyboard-shortcut-text">
                    Press <kbd>S</kbd> to toggle on/off.
                  </span>
                </li>
              )}
              <li>
                <b>Volume</b> - Adjustable volume for the metronome's click
                sounds.
              </li>
              <li>
                <b>Mute</b> - Mute all sounds.{" "}
                <span className="keyboard-shortcut-text">
                  Press <kbd>M</kbd> to toggle on/off.
                </span>
              </li>
              <li>
                <b>Reverb</b> - Adjustable reverb slider for adding some magic
                to the metronome's click.
              </li>
              <li>
                <b>Click styles</b> - Choose from 4 (handmade!) click
                sounds/styles.{" "}
                <span className="keyboard-shortcut-text">
                  Press <kbd>X</kbd> to cycle through click styles.
                </span>
              </li>
              <li>
                <b>Stress beat</b> - When turned on, the first beat of the bar
                will have a higher pitch. Additionally, you can press any beat
                dot to make it stressed. Note: while using Custom Song Structure
                mode, only the first beat will be stressed.{" "}
                <span className="keyboard-shortcut-text">
                  Press <kbd>B</kbd> to toggle on/off.
                </span>
              </li>
              <li>
                <b>Dial flash</b> - Visual beat indicator for the main BPM dial.{" "}
                <span className="keyboard-shortcut-text">
                  Press <kbd>D</kbd> to toggle on/off.
                </span>
              </li>
              <li>
                <b>Screen flash</b> - Visual beat indicator for the whole
                screen. When enabled, the screen will flash on each beat, making
                it possible to practice without sound. Warning: Using screen
                flash may trigger seizures for people with photosensitive
                epilepsy. Use this feature at your own discretion.{" "}
                <span className="keyboard-shortcut-text">
                  Press <kbd>F</kbd> to toggle on/off.
                </span>
              </li>
              <li>
                <b>Zen mode</b> - Hides all controls, dims the screen, and
                displays the large beat dots in the center of the screen for
                maximum focus.{" "}
                <span className="keyboard-shortcut-text">
                  Press <kbd>Z</kbd> to toggle Zen mode on/off.
                </span>
              </li>
              <li>
                <b>Timer</b> - When the timer is enabled, the metronome will
                play until the time runs out. You can set the time by clicking
                on the minutes and seconds.{" "}
                <span className="keyboard-shortcut-text">
                  Press <kbd>T</kbd> to toggle the Timer on/off.
                </span>
              </li>
              <li>
                <b>Color modes</b> - Choose between Dark and Light color modes.
              </li>
              <li>
                <b>Custom song structure</b> - Easily create a custom song
                structure with each bar of music having a custom time signature
                and tempo.{" "}
                <span className="keyboard-shortcut-text">
                  Press <kbd>C</kbd> to toggle on/off. Use the shortcuts{" "}
                  <kbd>←</kbd> and <kbd>→</kbd> to navigate between bars, and{" "}
                  <kbd>R</kbd> to reset to bar 1.
                </span>{" "}
                Song structures can be exported and imported. Note: while this
                feature is enabled, changing the BPM and Time Signature, or
                using Tap Tempo, will be disabled.
              </li>
              <li>
                <b>Progressive Tempo</b> - Set an increment and frequency, and
                the metronome will automatically increase the tempo by the
                increment, at the frequency you set (e.g., +2 BPM every second
                bar).{" "}
                <span className="keyboard-shortcut-text">
                  Press <kbd>P</kbd> to toggle on/off.
                </span>
              </li>
              <li>
                <b>Beat numbers</b> - Toggle on to show the beat numbers above the beat dots.{" "}
              </li>
              <li>
                <b>Keyboard shortcuts</b> - Almost every setting in Polytrome
                can be accessed via a keyboard shortcut.{" "}
                <span className="keyboard-shortcut-text">
                  Check the keyboard shortcuts menu in the top-right for all
                  shortcuts, or press <kbd>Tab</kbd> to access them while in the
                  app.
                </span>
              </li>
            </ul>
          </div>
          <div
            className={`about-section fade-in-up ${
              textVisible ? "visible" : ""
            }`}
          >
            <h2>Why I created Polytrome</h2>
            <p>
              While struggling to find a metronome that met all my needs in
              terms of features, I stumbled upon the idea of creating my own.
              Being a musician almost all my life, and a beginner drummer as of
              recently, I felt that there was a void on the web when it came to
              simple, snappy, and feature-rich metronome apps. I therefore made
              Polytrome primarily for myself, to improve my drumming (and by
              drumming, I mean blast beats). I decided to make it public and
              free because I believe everyone should have access to free and
              comprehensive music theory resources.
            </p>
            <b>
              I will try to keep Polytrome free for as long as possible. If
              you've found it useful, please consider supporting Polytrome on
              Ko-fi.
            </b>
          </div>

          {/* <div
            className={`about-section fade-in-up ${
              textVisible ? "visible" : ""
            }`}
          >
            <h2>Android App</h2>
            <p>
              The Polytrome Android app is in the works, hopefully out in 2026.
              Until then, use the web app on a browser on your phone and it
              should work just fine!
            </p>
          </div> */}

          <div
            className={`about-section fade-in-up ${
              textVisible ? "visible" : ""
            }`}
          >
            <h2>Contact</h2>
            <p>
              If you have a feature request or you found a bug, feel free to
              send me an email or drop me a message on LinkedIn.
            </p>
            <p style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {MdOutlineEmail({})}{" "}
              <a
                href="mailto:pavle@polytrome.com"
                style={{
                  marginLeft: 6,
                  color: "var(--text-primary)",
                  textDecoration: "underline",
                }}
              >
                pavle@polytrome.com
              </a>
            </p>
            <p style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {FaLinkedin({})}{" "}
              <a
                href="https://www.linkedin.com/in/pavlegospavic/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  marginLeft: 6,
                  color: "var(--text-primary)",
                  textDecoration: "underline",
                }}
              >
                LinkedIn
              </a>
            </p>
          </div>
        </div>
      </div>

      <footer
        className={`about-footer fade-in-up ${footerVisible ? "visible" : ""}`}
      >
        <p>
          Made with{" "}
          {FaHeart({
            className: "about-heart",
            style: { color: "var(--text-primary)", margin: "0 4px" },
          })}{" "}
          by Pavle Gospavic
        </p>
        <p style={{ fontSize: "0.9em", opacity: 0.7, marginTop: "8px" }}>
          v{packageJson.version}
          {/* Major            - Minor                               - Patch */}
          {/* Breaking changes - New features (backwards compatible) - Bug fixes (backwards compatible) */}
        </p>
      </footer>

      <ChangelogModal
        isOpen={isChangelogOpen}
        onClose={() => setIsChangelogOpen(false)}
      />
    </div>
  );
}
